import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, filter, switchMap, take, timeout } from 'rxjs/operators';

import { environment } from 'environments/environment';
import { AuthenticationService } from '@common/services';

// Tiempo máximo que una petición espera al token nuevo antes de salir igual
const SESSION_RENEWAL_WAIT_MS = 15000;

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  /**
   *
   * @param {AuthenticationService} _authenticationService
   */
  constructor(private _authenticationService: AuthenticationService) {}

  /**
   * Add auth header with jwt if user is logged in and request is to api url
   * @param request
   * @param next
   */
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isApiUrl = (environment.localUrl ? (request.url.startsWith(environment.apiUrl)
    || request.url.startsWith(environment.localUrl)) : request.url.startsWith(environment.apiUrl))
      && !request.url.includes('/api/translate/all');

    // Las peticiones que no van a nuestras apis salen tal cual
    if (!isApiUrl) return next.handle(request);

    // Las que traen su propia autorización ya eligieron con qué credencial viajan:
    // son las que arman la sesión (login, verificación del código, usuario y empresa
    // logueados). No se les pisa la cabecera ni se las hace esperar a la sesión,
    // porque son justamente las que la producen
    if (request.headers.has('Authorization')) return next.handle(request);

    // Si el token ya sirve, o si no hay ninguna renovación en curso que esperar
    // (login, usuario anónimo), la petición sale de inmediato
    if (this._authenticationService.hasValidToken || !this._authenticationService.isRenewingSession) {
      return next.handle(this.addAuthHeaders(request));
    }

    // Al abrir o recargar la aplicación las pantallas piden datos antes de que el
    // proveedor de identidad devuelva el token nuevo. Esas peticiones se retienen
    // hasta tenerlo, porque saldrían con el token viejo y el 401 resultante expulsa
    // al usuario hacia la página de no autorizado
    return this._authenticationService.onSessionResolved.pipe(
      filter((resolved) => resolved),
      take(1),
      timeout(SESSION_RENEWAL_WAIT_MS),
      catchError(() => of(false)),
      switchMap(() => next.handle(this.addAuthHeaders(request)))
    );
  }

  /**
   * Agrega la cabecera de autorización y la empresa activa a la petición
   *
   * @param request Petición a la que se le agregan las cabeceras
   */
  private addAuthHeaders(request: HttpRequest<any>): HttpRequest<any> {
    // Sin sesión no hay cabeceras que agregar
    if (!this._authenticationService.isAuthenticated()) return request;

    let headers: { [name: string]: string } = {};

    // Add authorization header with jwt token if available
    headers['Authorization'] = `Bearer ${this._authenticationService.token}`;

    // Add company id header if available
    if (this._authenticationService.currentCompanyIdValue) {
      headers['cid'] = `${this._authenticationService.currentCompanyIdValue}`;
    }

    return request.clone({
      setHeaders: headers
    });
  }
}
